import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DayPicker, type DateRange } from 'react-day-picker';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Assistance } from '@/components/shared/assistance';
import { Icon } from '@/components/ui/icon';

// ── Shared class names & formatter ────────────────────────────────────────────

export const calendarClassNames = {
  root: 'relative w-full',
  months: 'w-full',
  month: 'w-full',
  month_caption: 'mb-4 flex h-9 items-center justify-center',
  caption_label:
    'font-pp-neue-montreal text-[16px] font-semibold tracking-[0.25px] text-jncquoi-neutral-1000',
  nav: 'absolute inset-x-0 top-0 flex items-center justify-around px-2',
  button_previous:
    'group flex size-9 cursor-pointer items-center justify-center rounded-full text-jncquoi-neutral-1000 hover:bg-jncquoi-neutral-50 aria-disabled:cursor-default aria-disabled:pointer-events-none',
  button_next:
    'flex size-9 cursor-pointer items-center justify-center rounded-full text-jncquoi-neutral-1000 hover:bg-jncquoi-neutral-50 aria-disabled:cursor-default aria-disabled:pointer-events-none',
  chevron: 'size-6 fill-jncquoi-neutral-1000 group-aria-disabled:fill-jncquoi-neutral-500',
  month_grid: 'w-full table-fixed border-collapse',
  weekdays: '',
  weekday:
    'font-pp-neue-montreal uppercase pb-2 text-center text-[11px] font-medium tracking-[0.5px] text-[#9a9a9a]',
  week: '',
  day: 'p-[2px] text-center',
  day_button:
    'font-pp-neue-montreal mx-auto flex size-9 w-full cursor-pointer items-center justify-center rounded-[2px] text-[20px] transition-colors hover:bg-jncquoi-neutral-50 hover:text-jncquoi-neutral-1000 [aria-disabled="true"]:cursor-default [aria-disabled="true"]:pointer-events-none',
  today: 'font-semibold',
  selected: 'bg-jncquoi-neutral-1000 text-white rounded-[2px]',
  outside: '!text-jncquoi-neutral-300',
  disabled: '!text-jncquoi-neutral-600',
};

export const calendarWeekdayFormatter = (weekday: Date) =>
  weekday.toLocaleDateString('en', { weekday: 'short' }).toUpperCase();

// ── Day helpers ───────────────────────────────────────────────────────────────

/** Timezone-safe day identity key (`YYYY-MM-DD`), for comparing calendar days by value, not reference. */
const toDayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const firstOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);

// ── Navigation hook ───────────────────────────────────────────────────────────

interface UseCalendarNavigationOptions {
  isOpen: boolean;
  selected: Date | null | undefined;
  /** Lower paging bound (first-of-month). Defaults to the current month. Set to the earliest whitelisted day. */
  minMonth?: Date;
  /** Upper paging bound (first-of-month). Unbounded when absent. Set to the latest whitelisted day. */
  maxMonth?: Date;
}

export function useCalendarNavigation({
  isOpen,
  selected,
  minMonth,
  maxMonth,
}: UseCalendarNavigationOptions) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startMonth = minMonth ?? new Date(today.getFullYear(), today.getMonth(), 1);

  const [currentMonth, setCurrentMonth] = useState<Date>(startMonth);
  const touchStartX = useRef(0);

  useEffect(() => {
    if (!isOpen) return;
    if (selected && selected >= today) {
      setCurrentMonth(new Date(selected.getFullYear(), selected.getMonth(), 1));
    } else {
      setCurrentMonth(startMonth);
    }
  }, [isOpen]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) < 50) return;
    if (delta > 0) {
      setCurrentMonth(prev => {
        const candidate = new Date(prev.getFullYear(), prev.getMonth() + 1, 1);
        return !maxMonth || candidate <= maxMonth ? candidate : prev;
      });
    } else {
      setCurrentMonth(prev => {
        const candidate = new Date(prev.getFullYear(), prev.getMonth() - 1, 1);
        return candidate >= startMonth ? candidate : prev;
      });
    }
  };

  return {
    today,
    startMonth,
    endMonth: maxMonth,
    currentMonth,
    setCurrentMonth,
    handleTouchStart,
    handleTouchEnd,
  };
}

// ── Calendar (DayPicker only, no Drawer) ──────────────────────────────────────

interface CalendarProps {
  isOpen: boolean;
  selected: Date | undefined;
  onSelect: (date: Date | undefined) => void;
  assistance?: { title: string; description: string; descriptionForMember?: string };
  locale?: string;
  /**
   * Optional whitelist of selectable days. When provided (non-empty), every day
   * NOT in the list is disabled, and paging is bounded to the whitelist window
   * (earliest → latest day). When omitted, only past days are disabled (default).
   */
  enabledDates?: Date[];
}

export function Calendar({ isOpen, selected, onSelect, assistance, locale, enabledDates }: CalendarProps) {
  const hasWhitelist = !!enabledDates && enabledDates.length > 0;

  const allowedKeys = useMemo(() => new Set((enabledDates ?? []).map(toDayKey)), [enabledDates]);

  const { minMonth, maxMonth } = useMemo(() => {
    if (!hasWhitelist) return { minMonth: undefined, maxMonth: undefined };
    const times = enabledDates!.map(d => d.getTime());
    return {
      minMonth: firstOfMonth(new Date(Math.min(...times))),
      maxMonth: firstOfMonth(new Date(Math.max(...times))),
    };
  }, [enabledDates, hasWhitelist]);

  const { today, startMonth, endMonth, currentMonth, setCurrentMonth, handleTouchStart, handleTouchEnd } =
    useCalendarNavigation({ isOpen, selected, minMonth, maxMonth });

  const disabled = hasWhitelist ? (day: Date) => !allowedKeys.has(toDayKey(day)) : { before: today };

  return (
    <div
      className="flex flex-col gap-6 px-4 pt-4 pb-6"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <DayPicker
        mode="single"
        selected={selected}
        onSelect={onSelect}
        disabled={disabled}
        startMonth={startMonth}
        endMonth={endMonth}
        month={currentMonth}
        onMonthChange={setCurrentMonth}
        ISOWeek
        lang={locale}
        classNames={calendarClassNames}
        formatters={{ formatWeekdayName: calendarWeekdayFormatter }}
      />
      {assistance && <Assistance {...assistance} />}
    </div>
  );
}

// ── CalendarSheet (Drawer + header + Calendar) ────────────────────────────────

const CloseIcon = () => (
  <Icon name="xmark" width={24} height={24} strokeWidth={1.5} className="text-jncquoi-neutral-1000" />
);

interface CalendarSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  selected: Date | undefined;
  onSelect: (date: Date | undefined) => void;
  assistance?: { title: string; description: string; descriptionForMember?: string };
  /** Optional whitelist of selectable days — forwarded to `Calendar` (see its docs). */
  enabledDates?: Date[];
  /**
   * When provided, renders a confirm footer button (same as `CalendarRangeSheet`)
   * instead of selecting immediately on tap.
   */
  onConfirm?: () => void;
  selectLabel?: string;
  /** Overrides the default confirm-button disabled logic (`!selected`). */
  confirmDisabled?: boolean;
}

export function CalendarSheet({
  isOpen,
  onClose,
  title,
  subtitle,
  selected,
  onSelect,
  assistance,
  enabledDates,
  onConfirm,
  selectLabel,
  confirmDisabled,
}: CalendarSheetProps) {
  return (
    <Drawer
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DrawerContent className="border-jncquoi-neutral-200 h-[80dvh] min-h-[min(650px,100dvh)] border-t-[0.5px]">
        {title ? (
          <div className="flex shrink-0 flex-col gap-2 px-4 pt-8">
            <div className="flex items-center gap-8">
              <h2 className="font-pp-neue-montreal flex-1 text-[32px] font-medium tracking-[0.5px] text-[#2a2a2a]">
                {title}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="flex size-11 cursor-pointer items-center justify-center rounded-full"
                aria-label="Close"
              >
                <CloseIcon />
              </button>
            </div>
            {subtitle && (
              <p className="font-pp-neue-montreal text-[16px] tracking-[0.25px] text-[#707070]">{subtitle}</p>
            )}
          </div>
        ) : (
          <div className="flex shrink-0 items-center justify-end px-4 pt-4 pb-2">
            <button
              type="button"
              onClick={onClose}
              className="flex size-11 cursor-pointer items-center justify-center rounded-full"
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          <Calendar
            isOpen={isOpen}
            selected={selected}
            onSelect={onSelect}
            assistance={assistance}
            enabledDates={enabledDates}
          />
        </div>
        {onConfirm && (
          <div className="shrink-0 border-t-[0.5px] border-[#c8c8c8] bg-white px-4 pt-6 pb-10.5">
            <button
              type="button"
              disabled={confirmDisabled ?? !selected}
              onClick={onConfirm}
              className="font-pp-neue-montreal enabled:bg-jncquoi-neutral-1000 disabled:bg-jncquoi-neutral-600 flex h-13 w-full cursor-pointer items-center justify-center rounded-[2px] text-[12px] font-semibold tracking-[0.5px] text-white uppercase"
            >
              {selectLabel ?? 'SELECT'}
            </button>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
}

// ── CalendarRange (DayPicker range only, no Drawer) ───────────────────────────

export type { DateRange };

interface CalendarRangeProps {
  isOpen: boolean;
  selected: DateRange | undefined;
  onSelect: (range: DateRange | undefined) => void;
  assistance?: { title: string; description: string; descriptionForMember?: string };
}

export function CalendarRange({ isOpen, selected, onSelect, assistance }: CalendarRangeProps) {
  const { today, startMonth, currentMonth, setCurrentMonth, handleTouchStart, handleTouchEnd } =
    useCalendarNavigation({ isOpen, selected: selected?.from ?? null });

  return (
    <div
      className="flex flex-col gap-6 px-4 pt-4 pb-6"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <DayPicker
        mode="range"
        resetOnSelect
        selected={selected}
        onSelect={onSelect}
        disabled={{ before: today }}
        startMonth={startMonth}
        month={currentMonth}
        onMonthChange={setCurrentMonth}
        ISOWeek
        classNames={{
          ...calendarClassNames,
          range_middle: '!bg-jncquoi-neutral-200 !text-black rounded-none',
        }}
        formatters={{ formatWeekdayName: calendarWeekdayFormatter }}
      />
      {assistance && <Assistance {...assistance} />}
    </div>
  );
}

// ── CalendarRangeSheet (Drawer + header + CalendarRange + confirm button) ─────

interface CalendarRangeSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  selected: DateRange | undefined;
  onSelect: (range: DateRange | undefined) => void;
  selectLabel?: string;
  onConfirm: () => void;
  /**
   * When provided, overrides the default confirm-button disabled logic
   * (`!selected?.from`). Pass `true` to keep the button disabled even when
   * a start date is selected — useful when a minimum-nights rule must be met.
   */
  confirmDisabled?: boolean;
  assistance?: { title: string; description: string; descriptionForMember?: string };
}

export function CalendarRangeSheet({
  isOpen,
  onClose,
  title,
  subtitle,
  selected,
  onSelect,
  selectLabel,
  onConfirm,
  confirmDisabled,
  assistance,
}: CalendarRangeSheetProps) {
  return (
    <Drawer
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DrawerContent className="border-jncquoi-neutral-200 h-[80dvh] min-h-[min(650px,100dvh)] border-t-[0.5px]">
        {title ? (
          <div className="flex shrink-0 flex-col gap-2 px-4 pt-8">
            <div className="flex items-center gap-8">
              <h2 className="font-pp-neue-montreal flex-1 text-[32px] font-medium tracking-[0.5px] text-[#2a2a2a]">
                {title}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="flex size-11 cursor-pointer items-center justify-center rounded-full"
                aria-label="Close"
              >
                <CloseIcon />
              </button>
            </div>
            {subtitle && (
              <p className="font-pp-neue-montreal text-[16px] tracking-[0.25px] text-[#707070]">{subtitle}</p>
            )}
          </div>
        ) : (
          <div className="flex shrink-0 items-center justify-end px-4 pt-4 pb-2">
            <button
              type="button"
              onClick={onClose}
              className="flex size-11 cursor-pointer items-center justify-center rounded-full"
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          <CalendarRange isOpen={isOpen} selected={selected} onSelect={onSelect} assistance={assistance} />
        </div>
        <div className="shrink-0 border-t-[0.5px] border-[#c8c8c8] bg-white px-4 pt-6 pb-10.5">
          <button
            type="button"
            disabled={confirmDisabled ?? !selected?.from}
            onClick={onConfirm}
            className="font-pp-neue-montreal enabled:bg-jncquoi-neutral-1000 disabled:bg-jncquoi-neutral-600 flex h-13 w-full cursor-pointer items-center justify-center rounded-[2px] text-[12px] font-semibold tracking-[0.5px] text-white uppercase"
          >
            {selectLabel ?? 'SELECT'}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
